<template>
    <div :class="{ 'w-36': !showChangesCount, 'min-w-36': showChangesCount }" @keydown="keydownWrapper">
        <Label
            :label="props.label"
            :input-id="inputId"
            :help-button-text="props.helpButtonText"
            :is-required="props.isRequired"
        />

        <div ref="triggerRef" class="flex flex-row mt-1" :class="[undoBackgroundStyles]">
            <Tooltip>
                <template #trigger="tooltipProps">
                    <button
                        :id="inputId"
                        ref="displayRef"
                        aria-haspopup="dialog"
                        class="grid items-center gap-1.5 border rounded-md h-[2.5rem] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-offset-1 px-2 w-full"
                        :class="[statusStyles, focusStatusStyles, gridColumnClasses]"
                        :aria-controls="paneId"
                        :aria-expanded="isExpanded === 'Expanded-Click' || isExpanded === 'Expanded-Keyboard'"
                        type="button"
                        v-bind="tooltipProps"
                        @click="
                            triggerHideTooltip();
                            toggleReveal(false);
                        "
                        @keydown="keydownTriggerButton"
                    >
                        <div class="size-5 flex flex-row items-center">
                            <slot name="icon" />
                        </div>

                        <div class="font-semibold text-2xl/none text-gray-700 tabular-nums">
                            {{ props.allocationCount }}
                        </div>

                        <div v-if="showChangesCount" class="flex flex-col gap-[0.1875rem] text-left">
                            <div
                                v-if="props.plusCount > 0"
                                class="text-sm/none text-emerald-700 font-semibold text-nowrap tabular-nums"
                            >
                                +&thinsp;{{ props.plusCount }}
                            </div>
                            <div
                                v-if="props.minusCount > 0"
                                class="text-sm/none text-red-700 font-semibold text-nowrap tabular-nums"
                            >
                                −&thinsp;{{ props.minusCount }}
                            </div>
                        </div>

                        <div v-if="!showChangesCount" class="text-left font-light truncate">
                            {{ $t('allocation-selection-filter-label') }}
                        </div>

                        <TriangleIcon
                            class="transition-transform size-5"
                            :class="[
                                isExpanded === 'Expanded-Click' || isExpanded === 'Expanded-Keyboard'
                                    ? '-rotate-90'
                                    : 'rotate-90',
                            ]"
                        />
                    </button>
                </template>

                <template #text>
                    <template v-if="props.minusCount === 0 && props.plusCount === 0">
                        <i18n
                            path="allocation-selection-filter-allocations-count-tooltip-no-changes"
                            :args="{ count: allocationCount }"
                        >
                            <template #allocationCount>
                                <span class="font-semibold">
                                    {{ allocationCount }}
                                </span>
                            </template>
                        </i18n>
                    </template>

                    <template v-if="props.minusCount === 0 && props.plusCount > 0">
                        <i18n
                            path="allocation-selection-filter-allocations-count-tooltip-plus-only"
                            :args="{ count: allocationCount, plusCount }"
                        >
                            <template #allocationCount>
                                <span class="font-semibold">
                                    {{ allocationCount }}
                                </span>
                            </template>

                            <template #plusCount>
                                <span class="font-semibold text-emerald-700">
                                    {{ props.plusCount }}
                                </span>
                            </template>
                        </i18n>
                    </template>

                    <template v-if="props.minusCount > 0 && props.plusCount === 0">
                        <i18n
                            path="allocation-selection-filter-allocations-count-tooltip-minus-only"
                            :count="allocationCount"
                            :args="{ count: allocationCount, minusCount }"
                        >
                            <template #allocationCount>
                                <span class="font-semibold">
                                    {{ props.allocationCount }}
                                </span>
                            </template>

                            <template #minusCount>
                                <span class="font-semibold text-red-700">
                                    {{ props.minusCount }}
                                </span>
                            </template>
                        </i18n>
                    </template>

                    <template v-if="props.minusCount > 0 && props.plusCount > 0">
                        <i18n
                            path="allocation-selection-filter-allocations-count-tooltip-both"
                            :args="{ count: allocationCount, minusCount, plusCount }"
                        >
                            <template #allocationCount>
                                <span class="font-semibold">
                                    {{ props.allocationCount }}
                                </span>
                            </template>

                            <template #plusCount>
                                <span class="font-semibold text-emerald-700">
                                    {{ props.plusCount }}
                                </span>
                            </template>

                            <template #minusCount>
                                <span class="font-semibold text-red-700">
                                    {{ props.minusCount }}
                                </span>
                            </template>
                        </i18n>
                    </template>
                </template>

                <template #header>
                    {{ $t('allocation-selection-filter-allocation-type', { allocationGroupType: props.type }) }}
                </template>
            </Tooltip>

            <Tooltip>
                <template #trigger="tooltipProps">
                    <button
                        v-if="hasUndoButton"
                        v-bind="tooltipProps"
                        class="flex flex-row items-center gap-1 w-max text-base p-[0.5625rem] rounded-md"
                        :class="[undoButtonStyles, undoButtonFocusStyles]"
                        type="button"
                        @click="
                            (event: MouseEvent) => {
                                triggerHideTooltip();
                                emit('click', event);
                            }
                        "
                    >
                        <UndoIcon class="w-4 h-4" />
                    </button>
                </template>

                <template #text>
                    <div class="w-max">
                        {{ $t('allocation-selection-filter-undo-changes') }}
                    </div>
                </template>
            </Tooltip>

            <div
                v-if="isExpanded === 'Expanded-Click' || isExpanded === 'Expanded-Keyboard'"
                :id="paneId"
                ref="contentRef"
                class="grid grid-cols-[1fr] w-full rounded-md bg-gray-50 text-gray-800 ring-1 ring-gray-300 z-30 overflow-y-auto shadow-md"
                :class="{
                    'focus:outline-gray-500 focus:outline-2': isExpanded === 'Expanded-Keyboard',
                    'focus:outline-none': isExpanded === 'Expanded-Click',
                }"
                :style="floatingStyles"
                role="dialog"
                :tabindex="-1"
            >
                <slot name="panel" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { nanoid } from 'nanoid';
import { computed, nextTick, provide, ref } from 'vue';
import { AllocationGroupType } from '../AllocationSelectionPanel/AllocationSelectionPanel.types';
import Label from '../Form/Label.vue';
import TriangleIcon from '../Icon/TriangleIcon.vue';
import UndoIcon from '../Icon/UndoIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';

export type AllocationSelectionFilterStatus =
    | 'No-Changes'
    | 'Changes-Events'
    | 'Changes-Projects'
    | 'Changes-External-Events';

type AllocationSelectionFilterProps = {
    status: AllocationSelectionFilterStatus;
    label: string;
    helpButtonText?: string;
    isRequired?: boolean;
    allocationCount: number;
    plusCount: number;
    minusCount: number;
    type: AllocationGroupType;
};

type AllocationSelectionFilterEmits = {
    (e: 'click', ev: MouseEvent): void;
};

const props = defineProps<AllocationSelectionFilterProps>();
const emit = defineEmits<AllocationSelectionFilterEmits>();

const inputId = nanoid();
const paneId = nanoid();

const isExpanded = ref<'Expanded-Click' | 'Expanded-Keyboard' | 'Not-Expanded'>('Not-Expanded');

const triggerRef = ref(null);
const contentRef = ref(null);
const displayRef = ref(null);
const { floatingStyles } = useFloating(triggerRef, contentRef, {
    open: isExpanded.value === 'Expanded-Click' || isExpanded.value === 'Expanded-Keyboard',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
        offset(4),
        flip(),
        size({
            apply({ elements, rects, availableHeight, availableWidth }) {
                Object.assign(elements.floating.style, {
                    width: `${availableWidth - 16}px`,
                    minWidth: `${rects.reference.width}px`,
                    maxWidth: '60rem',
                    maxHeight: `${availableHeight - 16}px`,
                });
            },
        }),
    ],
});

const hasUndoButton = computed(
    () =>
        props.status === 'Changes-Events' ||
        props.status === 'Changes-Projects' ||
        props.status === 'Changes-External-Events',
);

const showChangesCount = computed(() => props.plusCount > 0 || props.minusCount > 0);

const undoButtonStyles = computed(() => {
    switch (props.status) {
        case 'No-Changes':
            return undefined;

        case 'Changes-Events':
            return `
                border border-sky-700 bg-sky-700 text-sky-50
                hover:border-sky-800 hover:bg-sky-800 hover:text-sky-50
                active:border-sky-900 active:bg-sky-900 active:text-sky-50
            `;

        case 'Changes-Projects':
            return `
                border border-rose-700 bg-rose-700 text-rose-50
                hover:border-rose-800 hover:bg-rose-800 hover:text-rose-50
                active:border-rose-900 active:bg-rose-900 active:text-rose-50
            `;

        case 'Changes-External-Events':
            return `
                border border-green-700 bg-green-700 text-green-50
                hover:border-green-800 hover:bg-green-800 hover:text-green-50
                active:border-green-900 active:bg-green-900 active:text-green-50
            `;
    }
});

const undoButtonFocusStyles = computed(() => {
    switch (props.status) {
        case 'No-Changes':
            return undefined;

        case 'Changes-Events':
            return 'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-offset-1 focus-visible:ring-sky-100 focus-visible:ring-offset-transparent';

        case 'Changes-Projects':
            return 'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-offset-1 focus-visible:ring-rose-100 focus-visible:ring-offset-transparent';

        case 'Changes-External-Events':
            return 'focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-offset-1 focus-visible:ring-green-100 focus-visible:ring-offset-transparent';
    }
});

const statusStyles = computed(() => {
    switch (props.status) {
        case 'No-Changes':
            return 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-gray-100 hover:text-gray-700 hover:border-gray-400 active:bg-gray-50 active:border-gray-500';

        case 'Changes-Events':
            return 'bg-sky-100 text-sky-700 border-sky-700 hover:bg-sky-200 hover:text-sky-800 active:bg-sky-100 active:text-sky-700 active:border-sky-900';

        case 'Changes-Projects':
            return 'bg-rose-100 text-rose-700 border-rose-700 hover:bg-rose-200 hover:text-rose-800 active:bg-rose-100 active:text-rose-700 active:border-rose-900';

        case 'Changes-External-Events':
            return 'bg-green-100 text-green-700 border-green-700 hover:bg-green-200 hover:text-green-800 active:bg-green-100 active:text-green-700 active:border-green-900';
    }
});

const undoBackgroundStyles = computed(() => {
    switch (props.status) {
        case 'No-Changes':
            return undefined;

        case 'Changes-Events':
            return 'rounded-md bg-sky-700 text-sky-100';

        case 'Changes-Projects':
            return 'rounded-md bg-rose-700 text-rose-100';

        case 'Changes-External-Events':
            return 'rounded-md bg-green-700 text-green-100';
    }
});

const focusStatusStyles = computed(() => {
    switch (props.status) {
        case 'No-Changes':
            return 'focus-visible:ring-gray-700 focus-visible:ring-offset-gray-100';

        case 'Changes-Events':
            return 'focus-visible:ring-sky-700 focus-visible:ring-offset-sky-100';

        case 'Changes-Projects':
            return 'focus-visible:ring-rose-700 focus-visible:ring-offset-rose-100';

        case 'Changes-External-Events':
            return 'focus-visible:ring-green-700 focus-visible:ring-offset-green-100';
    }
});

const gridColumnClasses = computed(() => {
    if (showChangesCount.value) {
        return 'grid-cols-[min-content_min-content_minmax(min-content,1fr)_min-content]';
    }

    return 'grid-cols-[min-content_min-content_minmax(0,1fr)_min-content]';
});

onClickOutside(contentRef, hide, { ignore: [displayRef] });

function keydownTriggerButton(event: KeyboardEvent) {
    switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case ' ':
            toggleReveal(true);
            event.preventDefault();
            break;
    }
}

function keydownWrapper(event: KeyboardEvent) {
    switch (event.key) {
        case 'Escape':
            // This workaround is required to not close the drop down if a combobox component within is closed with Esc (so it basically "stops" the "Esc pressed" event)
            if (event.target && (event.target as HTMLElement).dataset.headlessuiState !== 'open') {
                hide();
                event.preventDefault();
            }
            break;
    }
}

async function toggleReveal(isKeyboardEvent: boolean) {
    if (isExpanded.value === 'Expanded-Click' || isExpanded.value === 'Expanded-Keyboard') {
        await hide();
    } else {
        await reveal(isKeyboardEvent);
    }
}

async function reveal(isKeyboardEvent: boolean) {
    isExpanded.value = isKeyboardEvent ? 'Expanded-Keyboard' : 'Expanded-Click';

    await nextTick();

    activate();
}

async function hide() {
    isExpanded.value = 'Not-Expanded';

    await nextTick();

    deactivate();
}

defineExpose({
    hide,
});

const { activate, deactivate } = useFocusTrap(contentRef, {
    immediate: true,
    clickOutsideDeactivates: true,
    returnFocusOnDeactivate: true,
    allowOutsideClick: true,
    initialFocus: () => contentRef.value ?? undefined,
    onPostDeactivate: () => {
        triggerHideTooltip();
    },
});

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}
</script>
